<template>
  <div class="pt-0 pr-1 pl-1">
    <div class="no-gutters mt-2 mb-2 ml-3 mr-3">
      <div class="row">
        <div class="col-6">
          <div class="parameter-box">
            <h5 v-b-popover.hover.bottom="latitude + ' ' + longitude">{{ position }}</h5>
            <div class="parameter-name">Current position</div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-3 d-none d-sm-block">
          <span class="ml-2 dashboard-group-header">Scientific data:</span>
        </div>
        <div class="col-9 d-none d-sm-block">
          <hr class="mr-2 dashboard-group-header-hr">
        </div>
        <div class="col-6 d-block d-sm-none">
          <span class="ml-2 dashboard-group-header">Scientific data:</span>
        </div>
        <div class="col-6 d-block d-sm-none">
          <hr class="mr-2 dashboard-group-header-hr">
        </div>
      </div>
      <div class="row">
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="air_temperature !== null">
              <a href="#" class="parameter-link" @click="selectParameter(101, 3)" title="Show air temperature in chart and map">{{ showDigits(air_temperature, 1) }}</a> <small>°C</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Air temperature</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="air_pressure !== null">
              <a href="#" class="parameter-link" @click="selectParameter(101, 7)" title="Show air pressure in chart and map">{{ showDigits(air_pressure, 0) }}</a> <small>hPa</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Air pressure</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="rel_humidity !== null">
              <a href="#" class="parameter-link" @click="selectParameter(101, 4)" title="Show relative humidity in chart and map">{{ showDigits(rel_humidity, 1) }}</a> <small>%</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Relative humidity</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="atm_xco2 !== null">
              <a href="#" class="parameter-link" @click="selectParameter(101, 16)" title="Show atmospheric xCO2 in chart and map">{{ showDigits(atm_xco2, 1) }}</a> <small>ppm</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Atmospheric (xCO<sub>2</sub>)</div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="water_temperature !== null">
              <a href="#" class="parameter-link" @click="selectParameter(102, 1)" title="Show water temperature in chart and map">{{ showDigits(water_temperature, 1) }}</a> <small>°C</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Water temperature</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="conductivity !== null">
              <a href="#" class="parameter-link" @click="selectParameter(102, 3)" title="Show conductivity in chart and map">{{ showDigits(conductivity, 1) }}</a> <small>S/m</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Conductivity</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="dissolved_oxygen !== null">
              <a href="#" class="parameter-link" @click="selectParameter(102, 4)" title="Show dissolved oxygen in chart and map">{{ showDigits(dissolved_oxygen, 1) }}</a> <small>ml/l</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Dissolved oxygen</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="water_xco2 !== null">
              <a href="#" class="parameter-link" @click="selectParameter(102, 6)" title="Show oxygen in chart and map">{{ showDigits(water_xco2, 1) }}</a> <small>ppm</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Water (xCO<sub>2</sub>)</div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="wave_height_max !== null">
              <a href="#" class="parameter-link" @click="selectParameter(102, 20)" title="Show maximum wave height in chart and map">{{ showDigits(wave_height_max, 2) }}</a> <small>m</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Maximum wave height</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="wave_height_significant !== null">
              <a href="#" class="parameter-link" @click="selectParameter(102, 21)" title="Show significant wave height in chart and map">{{ showDigits(wave_height_significant, 2) }}</a> <small>m</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Significant wave heigh</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="wave_peak_direction !== null">
              <a href="#" class="parameter-link" @click="selectParameter(102, 22)" title="Show wave peak direction in chart and map">{{ showDigits(wave_peak_direction, 0) }}</a><small>°</small>
              <font-awesome-icon :icon="['fas', 'location-arrow']" size="xs" class="ml-2" :style="{ transform: this.rotateArrow(wave_peak_direction), color: 'rgba(0, 0, 0, 0.25)' }" />
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Wave peak direction</div>
          </div>
        </div>
        <div class="col-3">
          <div class="parameter-box">
            <h4 v-if="wave_peak_period !== null">
              <a href="#" class="parameter-link" @click="selectParameter(102, 23)" title="Show wave peak period in chart and map">{{ showDigits(wave_peak_period, 1) }}</a> <small>s</small>
            </h4>
            <h4 v-else class="text-muted"><small>n/a</small></h4>
            <div class="parameter-name">Wave peak period</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'
import Helpers from '../../../mixins/helpers'

/**
 * Constant defining the time delay allowed for data to be displayed
 * @type {number} [ms]
 */
const UPDATE_TIMEOUT_MS = 60 * 60 * 8000

export default {
  name: 'BuoyCVOO1DashboardWidget',
  mixins: [
    Helpers
  ],
  props: ['platformId'],
  methods: {
    ...mapMutations(['select_parameter']),
    rotateArrow: function (heading) {
      return 'rotate(' + Number(Number(heading) - 45) + 'deg)'
    },
    selectParameter: function (entityId, parameterId) {
      this.select_parameter([this.platformId, entityId, parameterId])
    }
  },
  computed: {
    ...mapState(['platforms']),
    ...mapGetters(['currentUpdate', 'lastUpdate', 'parameter']),
    recentUpdate () {
      return (entityId, parameterId) => {
        const update = this.currentUpdate(this.platformId, entityId, parameterId)
        const parameter = this.parameter(this.platformId, entityId, parameterId)
        return ((update.length !== 0) && (this.updateAgeValid(update, parameter, UPDATE_TIMEOUT_MS))) ? Number(update[1]) : null
      }
    },
    position () {
      const position = this.getCoordinatesNautical(this.roundDigits(this.latitude, 5),
        this.roundDigits(this.longitude, 5))
      return position.lat + ' ' + position.lng
    },
    latitude () {
      // Latitude will always be visible
      const update = this.currentUpdate(this.platformId, 1, 1)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    longitude () {
      // Longitude will always be visible
      const update = this.currentUpdate(this.platformId, 1, 2)
      return (update.length !== 0) ? Number(update[1]) : 0
    },
    air_temperature () {
      return this.recentUpdate(101, 3)
    },
    air_pressure () {
      return this.recentUpdate(101, 7)
    },
    rel_humidity () {
      return this.recentUpdate(101, 4)
    },
    atm_xco2 () {
      return this.recentUpdate(101, 16)
    },
    water_temperature () {
      return this.recentUpdate(102, 1)
    },
    conductivity () {
      return this.recentUpdate(102, 3)
    },
    dissolved_oxygen () {
      return this.recentUpdate(102, 4)
    },
    water_xco2 () {
      return this.recentUpdate(102, 6)
    },
    wave_height_max () {
      return this.recentUpdate(102, 20)
    },
    wave_height_significant () {
      return this.recentUpdate(102, 21)
    },
    wave_peak_direction () {
      return this.recentUpdate(102, 22)
    },
    wave_peak_period () {
      return this.recentUpdate(102, 23)
    }
  }
}
</script>

<style lang="scss" scoped>

.parameter-box {
  padding: 6px 0 6px 12px;
}
.parameter-box h4 {
  margin-bottom: 2px;
}
.parameter-box h5 {
  margin-bottom: 2px;
}
.parameter-name {
  font-size: 11px;
  color: #6c757d;
}
.parameter-link {
  color: #212529;
  text-decoration: none;
}
.state-box {
  font-size: 12px;
  font-weight: bold;
  text-align: center;
  vertical-align: bottom;
  border-radius: 2px;
  padding: 5px 2px 5px 2px;
}
.state-ok {
  background-color: #82E0AA;
  color: #186A3B;
}
.state-danger {
  background-color: #F1948A;
  color: #78281F;
}
.no-gutters {
  margin-right: 10px;
  margin-left: 10px;
  .col,
  [class*="col-"] {
    padding-right: 2px;
    padding-left: 2px;
  }
}
.dashboard-group-header {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.25);
  font-weight: bold;
}
.dashboard-group-header-hr {
  margin-block-end: 0;
}

</style>
